// built-from: /discovery-activity-detail v1 impl:discovery-activity-detail role:implementation
import { Link } from 'react-router-dom';
import type { ActivityInstance } from '../fixtures/modernisation/activities';

type Props = {
  activity: ActivityInstance;
  workspacePath: string;
  outputPath?: string;
};

const statePresentation = {
  proposed: { label: 'Proposed', action: 'Open VSM workspace', target: 'workspace' },
  planned: { label: 'Planned', action: 'Open VSM workspace', target: 'workspace' },
  'in-progress': { label: 'In progress', action: 'Continue VSM workspace', target: 'workspace' },
  'awaiting-review': { label: 'Awaiting review', action: 'Review output', target: 'output' },
  complete: { label: 'Complete', action: 'View reviewed output', target: 'output' },
} as const;

export function DiscoveryActivityDetail({ activity, workspacePath, outputPath }: Props): JSX.Element {
  const presentation = statePresentation[activity.state];
  const primaryPath = presentation.target === 'output' ? (outputPath ?? '#activity-output') : workspacePath;
  const sessionTitle = activity.state === 'planned' || activity.state === 'proposed'
    ? 'Not started'
    : activity.state === 'in-progress'
      ? 'Work in progress'
      : activity.state === 'awaiting-review'
        ? 'Output returned'
        : 'Output reviewed';
  const sessionDescription = activity.state === 'planned' || activity.state === 'proposed'
    ? 'No VSM session has been launched for this activity yet.'
    : activity.state === 'in-progress'
      ? 'The linked VSM session remains the source of truth for this activity.'
      : activity.state === 'awaiting-review'
        ? 'The latest returned version is ready for review.'
        : 'The latest output is eligible for later synthesis; it is not a finding.';

  return (
    <div className="journey-page journey-activity-detail">
      <nav className="journey-breadcrumb" aria-label="Breadcrumb">
        <Link to="/engagement/discovery">Discovery</Link>
        <span aria-hidden="true">/</span>
        <span>{activity.definition.name}</span>
      </nav>

      <header className="journey-page__header journey-page__header--action">
        <div>
          <div className="journey-title-line">
            <span className="journey-eyebrow">Discovery activity</span>
            <span className={`journey-status journey-status--${activity.state}`}>{presentation.label}</span>
          </div>
          <h1>{activity.definition.name}</h1>
          <p>{activity.definition.purpose}</p>
        </div>
        <Link className="journey-button journey-button--primary journey-button--large" to={primaryPath}>{presentation.action}</Link>
      </header>

      <div className="journey-detail-grid">
        <main className="journey-detail-main">
          <section className="journey-panel" aria-labelledby="why-title">
            <span className="journey-eyebrow">Why this is needed</span>
            <h2 id="why-title">{activity.question}</h2>
            <p className="journey-summary-copy">{activity.whyNow}</p>
          </section>

          <section className="journey-panel" aria-labelledby="inputs-title">
            <div className="journey-section-heading journey-section-heading--compact">
              <div><span className="journey-eyebrow">Before the session</span><h2 id="inputs-title">Inputs</h2></div>
            </div>
            <ul className="journey-check-list">
              {activity.inputs?.map((input) => <li key={input}>{input}</li>)}
            </ul>
          </section>

          <section className="journey-panel" aria-labelledby="completion-title">
            <div className="journey-section-heading journey-section-heading--compact">
              <div><span className="journey-eyebrow">Definition of done</span><h2 id="completion-title">Completion means</h2></div>
            </div>
            <ul className="journey-check-list">
              {activity.completionCriteria?.map((criterion) => <li key={criterion}>{criterion}</li>)}
            </ul>
          </section>
        </main>

        <aside className="journey-detail-aside">
          <section className="journey-panel" aria-labelledby="context-title">
            <span className="journey-eyebrow">Activity context</span>
            <h2 id="context-title">People and method</h2>
            <dl className="journey-definition-list">
              <dt>Owner</dt><dd>{activity.owner.name}</dd>
              <dt>Participants</dt><dd>{activity.participants.map((person) => person.name).join(', ')}</dd>
              <dt>Method</dt><dd>{activity.definition.method}</dd>
              <dt>Outcome</dt><dd>{activity.linkedOutcome?.name}</dd>
              <dt>Measure</dt><dd>{activity.linkedMeasure?.name}</dd>
            </dl>
          </section>

          <section id="activity-output" className="journey-panel" aria-labelledby="output-title">
            <span className="journey-eyebrow">Expected output</span>
            <h2 id="output-title">{activity.output.name}</h2>
            <p className="journey-summary-copy">The returned output will be reviewed before it becomes eligible for later synthesis; it is not itself a finding.</p>
          </section>

          <section className="journey-panel journey-session-state" aria-labelledby="session-title">
            <span className="journey-eyebrow">Session history</span>
            <h2 id="session-title">{sessionTitle}</h2>
            <p>{sessionDescription}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}
